import React from 'react';
import './client-answers.css';

function hasAnswer(value) {
  if (Array.isArray(value)) return value.some(hasAnswer);
  if (value && typeof value==='object') {
    return Object.entries(value).some(([key,item])=> key !=='dataUrl' && hasAnswer(item));
  }
  return String(value ?? '').trim().length > 0;
}

function formatFileSize(size=0) {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function AnswerValue({value}) {
  if (value===null || value===undefined || value==='') {
    return <span className="client-answer-empty-value">No answer provided</span>;
  }
  if (Array.isArray(value)) {
    if (value.length===0) return <span className="client-answer-empty-value">No answer provided</span>;
    if (value.every((item)=> typeof item !=='object' || item===null)) {
      return <ul className="client-answer-list">{value.map((item,index)=> <li key={`${item}-${index}`}>{String(item)}</li>)}</ul>;
    }
    return <div className="client-answer-records">{value.map((item,index)=> <div className="client-answer-record" key={item?.id || index}><AnswerValue value={item} /></div>)}</div>;
  }
  if (typeof value==='object') {
    if (value.name && ('dataUrl' in value || 'size' in value)) {
      return <div className="client-answer-file"><strong>{value.name}</strong>{value.size ? <small>{formatFileSize(value.size)}{value.type ? ` · ${value.type}` : ''}</small> : null}</div>;
    }
    if (Array.isArray(value.selected)) {
      return <div className="client-answer-object"><AnswerValue value={value.selected} />{value.otherDetails ? <p><strong>Additional details:</strong> {typeof value.otherDetails==='string' ? value.otherDetails : ''}</p> : null}</div>;
    }
    const entries=Object.entries(value).filter(([key,item])=> key !=='dataUrl' && hasAnswer(item));
    if (!entries.length) return <span className="client-answer-empty-value">No answer provided</span>;
    return <dl className="client-answer-nested">{entries.map(([key,item])=> <div key={key}><dt>{key.replace(/([A-Z])/g,' $1').replace(/^./,(letter)=> letter.toUpperCase())}</dt><dd><AnswerValue value={item} /></dd></div>)}</dl>;
  }
  return <span className="client-answer-text">{String(value)}</span>;
}

function TimelineAnswers({items}) {
  const rows=items.filter((item)=> hasAnswer(item.time) || hasAnswer(item.description) || hasAnswer(item.music) || hasAnswer(item.notes));
  if (!rows.length) return null;
  return (
    <div className="client-answer-timeline-wrap">
      <table className="client-answer-timeline">
        <thead>
          <tr>
            <th scope="col">Time</th>
            <th scope="col">Details</th>
            <th scope="col">Music</th>
            <th scope="col">Notes</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((item,index)=> (
            <tr key={`${item.time || 'moment'}-${index}`}>
              <td data-label="Time">{item.time || '—'}</td>
              <td data-label="Details"><AnswerValue value={item.description} /></td>
              <td data-label="Music"><AnswerValue value={item.music} /></td>
              <td data-label="Notes"><AnswerValue value={item.notes} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function countAnswers(answers) {
  return Object.values(answers).filter(hasAnswer).length;
}

function ClientAnswersPanel({event}) {
  const payload=event.clientAnswers || {};
  const answers=payload.answers || {};
  const sections=event.questionnaireSections || [];
  const knownIds=new Set();
  const answerGroups=sections.map((section)=> {
    const questions=(section.questions || [])
      .filter((question)=> {
        knownIds.add(question.id);
        return hasAnswer(answers[question.id]);
      })
      .map((question)=> ({id: question.id,title: question.title,value: answers[question.id]}));
    return {id: section.id,title: section.title,questions};
  }).filter((section)=> section.questions.length > 0);
  const ungrouped=Object.entries(answers)
    .filter(([id,value])=> !knownIds.has(id) && hasAnswer(value))
    .map(([id,value])=> ({id,title: id,value}));
  const hasTimeline=Array.isArray(payload.timeline) && payload.timeline.some(hasAnswer);
  const extras=[
    ['Song requests',payload.songs],
    ['Playlist links',payload.playlists]
  ].filter(([,value])=> Array.isArray(value) && value.some(hasAnswer));
  const answerCount=countAnswers(answers);
  const hasAnswers=answerGroups.length || ungrouped.length || extras.length || hasTimeline;

  return (
    <section className="panel client-answers-panel" aria-labelledby="client-answers-title">
      <div className="summary-title">
        <div>
          <span className="number">06</span>
          <div>
            <h2 id="client-answers-title">Client questionnaire answers</h2>
            <p>Client-provided details, organized by planner section.</p>
          </div>
        </div>
        <span className="client-answer-count">{hasAnswers ? `${answerCount} answers saved` : 'No answers yet'}</span>
      </div>
      {!hasAnswers ? (
        <div className="client-answers-empty">
          <strong>The client has not saved planner answers yet.</strong>
          <span>Responses will appear here after the client makes changes in their event planner.</span>
        </div>
      ) : (
        <div className="client-answer-groups">
          {answerGroups.map((group,index)=> (
            <section className="client-answer-group" key={group.id}>
              <div className="client-answer-group-heading">
                <span>{String(index + 1).padStart(2,'0')}</span>
                <h3>{group.title}</h3>
                <small>{group.questions.length} {group.questions.length===1 ? 'answer' : 'answers'}</small>
              </div>
              <dl className="client-answer-rows">
                {group.questions.map((answer)=> (
                  <div className="client-answer-row" key={answer.id}>
                    <dt>{answer.title}</dt>
                    <dd><AnswerValue value={answer.value} /></dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
          {ungrouped.length > 0 && (
            <section className="client-answer-group">
              <div className="client-answer-group-heading"><span>+</span><h3>Additional saved details</h3></div>
              <dl className="client-answer-rows">
                {ungrouped.map((answer)=> <div className="client-answer-row" key={answer.id}><dt>{answer.title}</dt><dd><AnswerValue value={answer.value} /></dd></div>)}
              </dl>
            </section>
          )}
          {hasTimeline && (
            <section className="client-answer-group">
              <div className="client-answer-group-heading"><span>+</span><h3>Timeline</h3></div>
              <TimelineAnswers items={payload.timeline} />
            </section>
          )}
          {extras.map(([title,value])=> (
            <section className="client-answer-group" key={title}>
              <div className="client-answer-group-heading"><span>+</span><h3>{title}</h3><small>{value.length} {value.length===1 ? 'item' : 'items'}</small></div>
              <AnswerValue value={value} />
            </section>
          ))}
        </div>
      )}
    </section>
  );
}

export default ClientAnswersPanel;